/* ==========================================================================
   Wine By the Case - global.js
   Shared behaviors: reveal-on-scroll, drawer and modal helpers with focus
   trapping, quantity steppers, AJAX quick add, and the wbtc:cart:updated
   event bus. Vanilla ES2020, no dependencies, loaded with defer.

   Markup contracts:
     [data-drawer-open="id"] / [data-drawer-close]  toggle .drawer elements
     [data-modal-open="id"]  / [data-modal-close]   toggle .modal-overlay elements
     [data-qty-minus] / [data-qty-plus]             step the input inside .qty
     [data-quick-add="variantId"]                   one-click add to cart
     [data-cart-count]                              live cart badge text

   Public API: window.WBTC (openDrawer, closeDrawer, openModal, closeModal,
   closeTop, publishCart, refreshCartCount, observeReveal, initReveal).
   ========================================================================== */
(function () {
  'use strict';

  const doc = document;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const routes = window.WBTCRoutes || {};
  const cartAddUrl = (routes.cartAdd || '/cart/add') + '.js';
  const cartUrl = (routes.cart || '/cart') + '.js';

  /* ------------------------------------------------------------------
     Focus utilities
     ------------------------------------------------------------------ */
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  function focusables(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
      return el.getClientRects().length > 0 && !el.closest('[hidden]');
    });
  }

  function focusFirst(container) {
    const target = container.querySelector('[data-autofocus]') || focusables(container)[0] || container;
    if (typeof target.focus === 'function') target.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------------
     Overlay stack: drawers and modals share Esc, Tab trap, scroll lock
     ------------------------------------------------------------------ */
  const openStack = [];
  let scrimEl = null;

  function scrim() {
    if (!scrimEl) {
      scrimEl = doc.createElement('div');
      scrimEl.className = 'drawer-scrim';
      scrimEl.setAttribute('aria-hidden', 'true');
      scrimEl.addEventListener('click', closeTop);
      doc.body.appendChild(scrimEl);
    }
    return scrimEl;
  }

  function lockScroll() {
    doc.documentElement.classList.add('no-scroll');
    doc.body.classList.add('no-scroll');
  }

  function unlockScrollIfIdle() {
    if (openStack.length) return;
    doc.documentElement.classList.remove('no-scroll');
    doc.body.classList.remove('no-scroll');
  }

  function syncTriggers(el, expanded) {
    if (!el.id) return;
    const selector = '[data-drawer-open="' + el.id + '"], [data-modal-open="' + el.id + '"]';
    doc.querySelectorAll(selector).forEach((btn) => {
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function isOpen(el) {
    return openStack.some((entry) => entry.el === el);
  }

  function openDrawer(el, trigger) {
    if (!el || isOpen(el)) return;
    openStack.push({ el, trigger: trigger || doc.activeElement, type: 'drawer' });
    el.classList.add('drawer--open');
    el.setAttribute('aria-hidden', 'false');
    syncTriggers(el, true);
    scrim().classList.add('is-open');
    lockScroll();
    focusFirst(el);
    el.dispatchEvent(new CustomEvent('wbtc:drawer:open', { bubbles: true }));
  }

  function openModal(el, trigger) {
    if (!el || isOpen(el)) return;
    openStack.push({ el, trigger: trigger || doc.activeElement, type: 'modal' });
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');
    syncTriggers(el, true);
    lockScroll();
    focusFirst(el);
    el.dispatchEvent(new CustomEvent('wbtc:modal:open', { bubbles: true }));
  }

  function closeOverlay(el) {
    const index = openStack.findIndex((entry) => entry.el === el);
    if (index === -1) return;
    const entry = openStack.splice(index, 1)[0];
    if (entry.trigger && typeof entry.trigger.focus === 'function' && doc.contains(entry.trigger)) {
      entry.trigger.focus({ preventScroll: true });
    }
    if (entry.type === 'drawer') {
      el.classList.remove('drawer--open');
      el.setAttribute('aria-hidden', 'true');
      if (!openStack.some((open) => open.type === 'drawer')) {
        scrim().classList.remove('is-open');
      }
    } else {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
    }
    syncTriggers(el, false);
    unlockScrollIfIdle();
    el.dispatchEvent(new CustomEvent('wbtc:' + entry.type + ':close', { bubbles: true }));
  }

  function closeTop() {
    if (!openStack.length) return;
    closeOverlay(openStack[openStack.length - 1].el);
  }

  function trapTab(event) {
    const top = openStack[openStack.length - 1];
    if (!top) return;
    const items = focusables(top.el);
    if (!items.length) {
      event.preventDefault();
      if (typeof top.el.focus === 'function') top.el.focus({ preventScroll: true });
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = doc.activeElement;
    if (!top.el.contains(active)) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  doc.addEventListener('keydown', (event) => {
    if (!openStack.length) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeTop();
    } else if (event.key === 'Tab') {
      trapTab(event);
    }
  });

  /* ------------------------------------------------------------------
     Quantity steppers
     ------------------------------------------------------------------ */
  function stepQuantity(button, direction) {
    const scope = button.closest('.qty') || button.closest('[data-qty]') || button.parentElement;
    if (!scope) return;
    const input = scope.querySelector('input');
    if (!input) return;
    const min = input.min === '' ? 1 : parseInt(input.min, 10);
    const max = input.max === '' ? Infinity : parseInt(input.max, 10);
    let value = parseInt(input.value, 10);
    if (Number.isNaN(value)) value = Number.isNaN(min) ? 1 : min;
    value += direction;
    if (!Number.isNaN(min)) value = Math.max(min, value);
    if (!Number.isNaN(max)) value = Math.min(max, value);
    input.value = String(value);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ------------------------------------------------------------------
     Cart event bus + AJAX quick add
     ------------------------------------------------------------------ */
  function publishCart(cart) {
    doc.dispatchEvent(new CustomEvent('wbtc:cart:updated', {
      detail: { item_count: cart.item_count, cart }
    }));
  }

  function refreshCartCount() {
    return fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then(publishCart)
      .catch(() => {});
  }

  function quickAdd(button) {
    if (button.disabled || button.classList.contains('is-loading')) return;
    const variantId = parseInt(button.getAttribute('data-quick-add'), 10);
    if (!variantId) return;
    if (!button.dataset.labelDefault) button.dataset.labelDefault = button.textContent.trim();
    button.disabled = true;
    button.classList.add('is-loading');
    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Add to cart failed: ' + res.status);
        return fetch(cartUrl, { headers: { Accept: 'application/json' } });
      })
      .then((res) => res.json())
      .then((cart) => {
        publishCart(cart);
        button.classList.add('is-added');
        button.textContent = 'Added ✓';
      })
      .catch(() => {
        button.classList.add('is-error');
        button.textContent = 'Try again';
      })
      .finally(() => {
        window.setTimeout(() => {
          button.disabled = false;
          button.classList.remove('is-loading', 'is-added', 'is-error');
          button.textContent = button.dataset.labelDefault;
        }, 1600);
      });
  }

  doc.addEventListener('wbtc:cart:updated', (event) => {
    const detail = event.detail || {};
    if (typeof detail.item_count !== 'number') return;
    doc.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(detail.item_count);
      el.classList.toggle('is-empty', detail.item_count === 0);
    });
  });

  /* ------------------------------------------------------------------
     Delegated clicks
     ------------------------------------------------------------------ */
  doc.addEventListener('click', (event) => {
    const drawerOpener = event.target.closest('[data-drawer-open]');
    if (drawerOpener) {
      event.preventDefault();
      const target = doc.getElementById(drawerOpener.getAttribute('data-drawer-open'));
      if (target) {
        if (isOpen(target)) closeOverlay(target);
        else openDrawer(target, drawerOpener);
      }
      return;
    }

    const modalOpener = event.target.closest('[data-modal-open]');
    if (modalOpener) {
      event.preventDefault();
      const target = doc.getElementById(modalOpener.getAttribute('data-modal-open'));
      if (target) {
        if (isOpen(target)) closeOverlay(target);
        else openModal(target, modalOpener);
      }
      return;
    }

    const closer = event.target.closest('[data-drawer-close], [data-modal-close]');
    if (closer) {
      event.preventDefault();
      const overlay = closer.closest('.drawer, .modal-overlay');
      if (overlay) closeOverlay(overlay);
      else closeTop();
      return;
    }

    const minus = event.target.closest('[data-qty-minus]');
    if (minus) {
      event.preventDefault();
      stepQuantity(minus, -1);
      return;
    }

    const plus = event.target.closest('[data-qty-plus]');
    if (plus) {
      event.preventDefault();
      stepQuantity(plus, 1);
      return;
    }

    const quick = event.target.closest('[data-quick-add]');
    if (quick) {
      event.preventDefault();
      quickAdd(quick);
    }
  });

  /* ------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------ */
  let revealObserver = null;

  function markIn(el) {
    el.classList.add('is-in');
  }

  /* An element sitting inside a horizontal scroller (the deals strip track) is
     clipped out of the viewport sideways, so IntersectionObserver never fires
     for it and it stays at opacity 0 forever. Scroll the strip and you get
     blank space where cards should be. Reveal those up front instead. */
  function inSideScroller(el) {
    for (let node = el.parentElement; node && node !== doc.body; node = node.parentElement) {
      const cs = getComputedStyle(node);
      const scrolls = cs.overflowX === 'auto' || cs.overflowX === 'scroll';
      if (scrolls && node.scrollWidth > node.clientWidth + 4) return true;
    }
    return false;
  }

  function initReveal() {
    const items = doc.querySelectorAll('.reveal:not(.is-in)');
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach(markIn);
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            markIn(entry.target);
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });
    }
    items.forEach((el) => {
      if (inSideScroller(el)) markIn(el);
      else revealObserver.observe(el);
    });
  }

  function observeReveal(el) {
    if (!el) return;
    if (revealObserver && !reduceMotion.matches && !inSideScroller(el)) revealObserver.observe(el);
    else markIn(el);
  }

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', (event) => {
      if (event.matches) doc.querySelectorAll('.reveal:not(.is-in)').forEach(markIn);
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function init() {
    initReveal();
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCartCount();
  });

  window.WBTC = Object.assign(window.WBTC || {}, {
    openDrawer,
    closeDrawer: closeOverlay,
    openModal,
    closeModal: closeOverlay,
    closeTop,
    focusables,
    publishCart,
    refreshCartCount,
    observeReveal,
    initReveal
  });
})();
